import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, Download, Copy, ListTodo, Plus, ChevronUp, ChevronDown, X, Link2, Filter, TableIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTranslation } from '@/i18n'
// Duplicated from index.tsx to avoid circular import
const EDITOR_MODE = 'editor mode'
const GRAPH_PREVIEW = 'graph preview'
const JSON_VIEW = 'json view'

interface SearchMatchInfo {
  total: number
  current: number
}

interface GraphEditorSubNavbarV2Props {
  activeTab: string
  hasBoundingBoxes: boolean
  onTabChange: (tab: string) => void
  pendingReviewCount?: number
  onSearchTermChange?: (term: string) => void
  searchMatchInfo?: SearchMatchInfo
  onSearchNext?: () => void
  onSearchPrevious?: () => void
  onDownload?: () => void
  onCopy?: () => void
  onAddObject?: () => void
  onPendingReviewClick?: () => void
  isPendingReviewActive?: boolean
  /** FS-623 labeling filter (by Category/Subcategory/Type/Tag/Annotation/Note). */
  onFilterClick?: () => void
  isFilterActive?: boolean
  /** The editable row/column grid for material balance tables. */
  onTablesClick?: () => void
  isTablesActive?: boolean
  onConnectedClick?: () => void
  isConnectedActive?: boolean
  /**
   * Show the Connected P&IDs entry point. Gated on whether the drawing actually
   * has connected P&IDs so the button doesn't take up navbar space (and can't
   * open an empty panel) when there's nothing to show.
   */
  showConnected?: boolean
  /** Gates the JSON tab's PARSED / Ground Truth toggle to internal-org users. */
  isInternalOrg?: boolean
  /** Annotators are read-only: hide the JSON tab. */
  isAnnotator?: boolean
  /** JSON tab view mode: the live annotation graph ('graph') vs the GT export ('gt'). */
  jsonMode?: 'graph' | 'gt'
  onJsonModeChange?: (mode: 'graph' | 'gt') => void
  /** A graph nobody edits here (an SLD page's parse): no review, no adding. */
  readOnly?: boolean
}

const TAB_KEYS = [EDITOR_MODE, GRAPH_PREVIEW, JSON_VIEW]

export default function GraphEditorSubNavbarV2({
  activeTab,
  hasBoundingBoxes,
  onTabChange,
  pendingReviewCount,
  onSearchTermChange,
  searchMatchInfo,
  onSearchNext,
  onSearchPrevious,
  onDownload,
  onCopy,
  onAddObject,
  onPendingReviewClick,
  isPendingReviewActive,
  onFilterClick,
  isFilterActive,
  onTablesClick,
  isTablesActive,
  onConnectedClick,
  isConnectedActive,
  showConnected,
  isInternalOrg,
  isAnnotator,
  jsonMode,
  onJsonModeChange,
  readOnly = false,
}: GraphEditorSubNavbarV2Props) {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearchActive, setIsSearchActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Bumped to request focus; the effect below runs after the input renders.
  const [focusRequest, setFocusRequest] = useState(0)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      onSearchTermChange?.(value)
    }, 300)
  }, [onSearchTermChange])

  const handleSearchClick = () => {
    setIsSearchActive(true)
    setFocusRequest((n) => n + 1)
  }

  useEffect(() => {
    if (focusRequest === 0) return
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [focusRequest])

  const handleSearchClose = () => {
    setSearchTerm('')
    onSearchTermChange?.('')
    setIsSearchActive(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchMatchInfo && searchMatchInfo.total > 0) {
      e.preventDefault()
      if (e.shiftKey) onSearchPrevious?.()
      else onSearchNext?.()
    }
    if (e.key === 'Escape') {
      handleSearchClose()
    }
  }

  // Reset search state when switching tabs
  useEffect(() => {
    handleSearchClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Cmd+F / Ctrl+F focuses this search on every tab (Editor Mode, Graph
  // Preview, JSON). The tab views skip their own shortcut while this owns search.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault()
        setIsSearchActive(true)
        setFocusRequest((n) => n + 1)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Cleanup debounce timer
  useEffect(() => () => clearTimeout(debounceRef.current), [])

  const getTabLabel = (key: string) => {
    switch (key) {
      case EDITOR_MODE: return t('data_catalog:graph_editor.tabs.editor_mode', { defaultValue: 'Editor Mode' })
      case GRAPH_PREVIEW: return t('data_catalog:graph_editor.tabs.graph_preview', { defaultValue: 'Graph Preview' })
      case JSON_VIEW: return t('data_catalog:graph_editor.tabs.json_view', { defaultValue: 'JSON' })
      default: return key
    }
  }

  // Annotators keep Graph Preview but not the raw JSON tab.
  const visibleTabs = TAB_KEYS.filter((key) => {
    if (isAnnotator && key === JSON_VIEW) return false
    return true
  })

  return (
    // Everything stays on ONE row. The tab strip never yields (shrink-0), and
    // the search box is the flexible element instead — it was pinned at 248px
    // in BOTH its states, which is what left the row nothing to give when the
    // JSON tab reveals the PARSED / Ground Truth toggle.
    //
    // `overflow-x-auto` is the last-resort fallback (greptile on #30978): once
    // the search hits its 132px floor nothing else can give, and without this
    // the right-hand actions would fall off the viewport and be unreachable.
    // It sits on the TOOLBAR, not on the pill group — a scrollbar inside an
    // h-8 group of pills is what looked broken, and it appeared on a plain tab
    // click. Here it can only appear once the row genuinely cannot fit, which
    // with a shrunken search is below roughly 1140px of editor width.
    <div className="flex items-center justify-between gap-3 overflow-x-auto px-6 py-3 border-b border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card shrink-0">
      {/* Left: Tabs
           UI-17: the labels used to wrap. In Portuguese "Graph Preview" is
           "Pré-visualização do gráfico" and "KG Preview" is
           "Pré-visualização de KG" — roughly double the English — so a pill's
           label broke onto a second line, made that pill taller than its
           neighbours and shifted the whole strip. Confirmed against Pegah's
           screenshot by switching the app to Portuguese.
           `whitespace-nowrap` on the buttons is the fix. `shrink-0` here keeps
           the primary nav at its natural size; the toolbar above wraps instead
           of squeezing it, so an unwrappable strip never has to scroll or
           shove the search box. */}
      <div className="bg-[#f5f5f5] dark:bg-dark-accent flex shrink-0 items-center p-[3px] rounded-lg h-8">
        {visibleTabs.map((tabKey) => {
          if (!hasBoundingBoxes && tabKey === EDITOR_MODE) return null
          const isActive = activeTab === tabKey
          return (
            <button
              key={tabKey}
              type="button"
              onClick={() => onTabChange(tabKey)}
              className={`px-3 text-sm font-medium whitespace-nowrap rounded-md transition-all h-full flex items-center ${
                isActive
                  ? 'bg-white dark:bg-dark-card border border-[#e5e5e5] dark:border-dark-border shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {getTabLabel(tabKey)}
            </button>
          )
        })}
      </div>

      {/* Right: Actions — min-w-0 + justify-end so this side absorbs whatever
           the tabs leave, and the search inside it is what actually shrinks. */}
      <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
        {/* JSON view toggle: live annotation graph vs GT export (internal-org, JSON tab only).
            NOTE: the JSON tab itself is visible to all non-annotator users, so this
            isInternalOrg guard is the real gate — it keeps non-internal
            users on the default PARSED view and out of GT mode. */}
        {activeTab === JSON_VIEW && isInternalOrg && jsonMode && onJsonModeChange && (
          <div className="bg-[#f5f5f5] dark:bg-dark-accent flex items-center p-[3px] rounded-lg h-8">
            {([['graph', t('graph_editor:jsonViewer.modeGraph', { defaultValue: 'PARSED' })], ['gt', t('graph_editor:jsonViewer.modeGroundTruth', { defaultValue: 'Ground Truth' })]] as const).map(([mode, label]) => {
              const isActive = jsonMode === mode
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => onJsonModeChange(mode)}
                  className={`px-3 text-sm font-medium whitespace-nowrap rounded-md transition-all h-full flex items-center ${
                    isActive
                      ? 'bg-white dark:bg-dark-card border border-[#e5e5e5] dark:border-dark-border shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
              )
            })}
          </div>
        )}
        {/* Search */}
        {isSearchActive ? (
          <div className="bg-white dark:bg-dark-card border border-[#e5e5e5] dark:border-dark-border rounded-lg flex min-w-[132px] w-full max-w-[248px] items-center gap-1.5 px-2.5">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              type="text"
              placeholder={String(t('common:buttons.search', { defaultValue: 'Search' }))}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 h-8 text-sm border-0 shadow-none focus-visible:ring-0 px-0"
            />
            {searchTerm && searchMatchInfo && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {searchMatchInfo.total > 0
                  ? `${searchMatchInfo.current + 1}/${searchMatchInfo.total}`
                  : t('graph_editor:search.noMatches', { defaultValue: 'No matches' })}
              </span>
            )}
            {searchMatchInfo && searchMatchInfo.total > 1 && (
              <>
                <button
                  type="button"
                  onClick={onSearchPrevious}
                  className="p-0.5 rounded hover:bg-[#f0f1f4] dark:hover:bg-dark-muted text-muted-foreground"
                  title={t('graph_editor:search.previousMatch', { defaultValue: 'Previous match (Shift+Enter)' })}
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={onSearchNext}
                  className="p-0.5 rounded hover:bg-[#f0f1f4] dark:hover:bg-dark-muted text-muted-foreground"
                  title={t('graph_editor:search.nextMatch', { defaultValue: 'Next match (Enter)' })}
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={handleSearchClose}
              className="p-0.5 rounded hover:bg-[#f0f1f4] dark:hover:bg-dark-muted text-muted-foreground"
              title={t('graph_editor:search.close', { defaultValue: 'Close (Escape)' })}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div
            className="bg-white dark:bg-dark-card border border-[#e5e5e5] dark:border-dark-border rounded-lg flex min-w-[132px] w-full max-w-[248px] items-center gap-1.5 px-2.5 py-1.5 cursor-text"
            onClick={handleSearchClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
          >
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-sm text-muted-foreground truncate">
              {String(t('common:buttons.search', { defaultValue: 'Search' }))}
            </span>
          </div>
        )}

        {/* Copy (JSON tab only) */}
        {onCopy && (
          <Button variant="outline" size="icon" className="size-8" onClick={onCopy}>
            <Copy className="w-4 h-4" />
          </Button>
        )}

        {/* Download — in the JSON tab's Ground Truth mode this downloads the GT export.
            Hidden when no handler is provided (annotators are read-only). */}
        {onDownload && (
          <Button variant="outline" size="icon" className="size-8" onClick={onDownload}>
            <Download className="w-4 h-4" />
          </Button>
        )}

        {/* Pending Review — absent on a read-only graph (an SLD page's parse) */}
        {!readOnly && (
          <Button
            variant="outline"
            size="sm"
            className={`h-8 gap-1.5 ${isPendingReviewActive ? 'border-primary text-primary bg-primary/5' : ''}`}
            onClick={onPendingReviewClick}
          >
            <ListTodo className="w-4 h-4" />
            <span className="text-sm font-medium">
              {t('data_catalog:graph_editor.pending_review', { defaultValue: 'Pending Review' })}
              {pendingReviewCount !== undefined && (
                <span className="text-muted-foreground"> ({pendingReviewCount})</span>
              )}
            </span>
          </Button>
        )}

        {/* FS-623: labeling filter by Category/Subcategory/Type/Tag/Annotation/Note */}
        {onFilterClick && (
          <Button
            variant="outline"
            size="sm"
            className={`h-8 gap-1.5 ${isFilterActive ? 'border-primary text-primary bg-primary/5' : ''}`}
            onClick={onFilterClick}
          >
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">
              {t('data_catalog:graph_editor.filter', { defaultValue: 'Filter' })}
            </span>
          </Button>
        )}

        {/* Material balance tables as an editable grid */}
        {onTablesClick && (
          <Button
            variant="outline"
            size="sm"
            className={`h-8 gap-1.5 ${isTablesActive ? 'border-primary text-primary bg-primary/5' : ''}`}
            onClick={onTablesClick}
          >
            <TableIcon className="w-4 h-4" />
            <span className="text-sm font-medium">
              {t('graph_editor:tableGrid.title', { defaultValue: 'Tables' })}
            </span>
          </Button>
        )}

        {/* Connected P&IDs — only when the drawing actually has connections */}
        {showConnected && (
          <Button
            variant="outline"
            size="sm"
            className={`h-8 gap-1.5 ${isConnectedActive ? 'border-primary text-primary bg-primary/5' : ''}`}
            onClick={onConnectedClick}
          >
            <Link2 className="w-4 h-4" />
            <span className="text-sm font-medium">
              {t('graph_editor:connectedPnids.title', { defaultValue: 'Connected P&IDs' })}
            </span>
          </Button>
        )}

        {/* Add Object — absent on a read-only graph */}
        {!readOnly && (
          <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={onAddObject}>
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">{t('data_catalog:graph_editor.add_object', { defaultValue: 'Add Object' })}</span>
          </Button>
        )}
      </div>
    </div>
  )
}
